import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import {
  View,
  Text,
  Pressable,
  Modal,
  StyleSheet,
  Platform,
  Animated,
  Dimensions,
} from 'react-native';

type AlertButton = {
  text: string;
  onPress?: () => void;
  style?: 'default' | 'cancel' | 'destructive';
};

type AlertType = 'error' | 'success' | 'warning' | 'info' | 'confirm';

interface AlertState {
  visible: boolean;
  title: string;
  message?: string;
  buttons: AlertButton[];
  type: AlertType;
}

type ShowAlertFn = (
  title: string,
  message?: string,
  buttons?: AlertButton[],
  type?: AlertType,
) => void;

const AlertContext = createContext<ShowAlertFn | null>(null);

let globalShowAlert: ShowAlertFn = (title, message, buttons) => {
  if (Platform.OS === 'web') {
    const result = window.confirm(`${title}${message ? `\n${message}` : ''}`);
    if (buttons && buttons.length > 1) {
      const action = result
        ? buttons.find(b => b.style !== 'cancel')
        : buttons.find(b => b.style === 'cancel');
      action?.onPress?.();
    } else if (buttons?.[0]) {
      buttons[0].onPress?.();
    }
  }
};

export const showThemedAlert: ShowAlertFn = (title, message, buttons, type) => {
  globalShowAlert(title, message, buttons, type);
};

const ACCENT_COLORS: Record<AlertType, string> = {
  error: '#EF4444',
  success: '#10B981',
  warning: '#F59E0B',
  info: '#3B82F6',
  confirm: '#40BFB4',
};

export const ThemedAlertProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [alert, setAlert] = useState<AlertState>({
    visible: false,
    title: '',
    message: undefined,
    buttons: [],
    type: 'info',
  });

  const opacity = useRef(new Animated.Value(0)).current;
  const scale = useRef(new Animated.Value(0.9)).current;

  const dismiss = useCallback(() => {
    Animated.parallel([
      Animated.timing(opacity, { toValue: 0, duration: 150, useNativeDriver: true }),
      Animated.timing(scale, { toValue: 0.9, duration: 150, useNativeDriver: true }),
    ]).start(() => {
      setAlert(prev => ({ ...prev, visible: false }));
    });
  }, [opacity, scale]);

  const show: ShowAlertFn = useCallback((title, message, buttons, type = 'info') => {
    if (Platform.OS === 'web') {
      const result = window.confirm(`${title}${message ? `\n${message}` : ''}`);
      if (buttons && buttons.length > 1) {
        const action = result
          ? buttons.find(b => b.style !== 'cancel')
          : buttons.find(b => b.style === 'cancel');
        action?.onPress?.();
      } else if (buttons?.[0]) {
        buttons[0].onPress?.();
      }
      return;
    }

    const resolvedButtons = buttons && buttons.length > 0 ? buttons : [{ text: 'OK' }];
    setAlert({ visible: true, title, message, buttons: resolvedButtons, type });
    opacity.setValue(0);
    scale.setValue(0.9);
    Animated.parallel([
      Animated.timing(opacity, { toValue: 1, duration: 200, useNativeDriver: true }),
      Animated.spring(scale, { toValue: 1, useNativeDriver: true, damping: 18, stiffness: 300 }),
    ]).start();
  }, [opacity, scale]);

  globalShowAlert = show;

  const handlePress = useCallback((button: AlertButton) => {
    dismiss();
    setTimeout(() => button.onPress?.(), 200);
  }, [dismiss]);

  const accentColor = ACCENT_COLORS[alert.type];

  return (
    <AlertContext.Provider value={show}>
      {children}
      <Modal
        visible={alert.visible}
        transparent
        animationType="none"
        statusBarTranslucent
        onRequestClose={dismiss}
      >
        <View style={styles.overlay}>
          <Animated.View style={[styles.card, { opacity, transform: [{ scale }] }]}>
            <View style={[styles.accentBar, { backgroundColor: accentColor }]} />

            <View style={styles.body}>
              <Text style={styles.title}>{alert.title}</Text>
              {alert.message ? (
                <Text style={styles.message}>{alert.message}</Text>
              ) : null}
            </View>

            <View style={[styles.buttonRow, alert.buttons.length === 1 && styles.buttonRowSingle]}>
              {alert.buttons.map((btn, i) => {
                const isCancel = btn.style === 'cancel';
                const isDestructive = btn.style === 'destructive';
                const btnBg = isCancel
                  ? 'transparent'
                  : isDestructive
                    ? '#EF4444'
                    : accentColor;
                const btnBorder = isCancel ? '#3A3D42' : 'transparent';
                const textColor = isCancel ? '#FFFFFF' : '#FFFFFF';

                return (
                  <Pressable
                    key={`${btn.text}-${i}`}
                    onPress={() => handlePress(btn)}
                    style={({ pressed }) => [
                      styles.button,
                      {
                        backgroundColor: btnBg,
                        borderColor: btnBorder,
                        borderWidth: isCancel ? 1 : 0,
                        opacity: pressed ? 0.7 : 1,
                      },
                      alert.buttons.length === 1 && styles.buttonFull,
                    ]}
                  >
                    <Text style={[styles.buttonText, { color: textColor }]}>
                      {btn.text}
                    </Text>
                  </Pressable>
                );
              })}
            </View>
          </Animated.View>
        </View>
      </Modal>
    </AlertContext.Provider>
  );
};

export const useThemedAlert = (): ShowAlertFn => {
  const ctx = useContext(AlertContext);
  if (!ctx) throw new Error('useThemedAlert must be used within ThemedAlertProvider');
  return ctx;
};

const { width } = Dimensions.get('window');

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  card: {
    width: Math.min(width - 64, 360),
    backgroundColor: '#292D33',
    borderRadius: 16,
    overflow: 'hidden',
  },
  accentBar: {
    height: 3,
    width: '100%',
  },
  body: {
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 16,
  },
  title: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: '700',
    fontFamily: 'PlusJakartaSans-Regular',
    marginBottom: 8,
    textAlign: 'center',
  },
  message: {
    color: '#B0B3B8',
    fontSize: 14,
    fontFamily: 'PlusJakartaSans-Regular',
    lineHeight: 20,
    textAlign: 'center',
  },
  buttonRow: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingBottom: 20,
    gap: 10,
  },
  buttonRowSingle: {
    justifyContent: 'center',
  },
  button: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  buttonFull: {
    flex: 0,
    paddingHorizontal: 48,
  },
  buttonText: {
    fontSize: 14,
    fontWeight: '600',
    fontFamily: 'PlusJakartaSans-Regular',
  },
});
